import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Fab,
    FormControlLabel,
    Switch,
} from '@mui/material';
import {
    Event as CalendarIcon,
    Add as AddIcon,
    ArrowBack as BackIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/Layout/PageLayout';
import RosterCalendar from '../../components/Roster/RosterCalendar';
import { getUserTimezone } from '../../utils/dateUtils';
import { getStaffDisplayName, getClientDisplayName } from '../../utils/nameUtils';

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);
import { rosterService, Shift } from '../../services/rosterService';
import { staffService } from '../../services/staffService';
import { clientService } from '../../services/clientService';
import { useAuth } from '../../contexts/AuthContext';
import { getUserPrivilegeLevel } from '../../utils/roleUtils';
import ShiftFormDialog from '../../components/Roster/ShiftFormDialog';

interface StaffOption {
    value: number;
    label: string;
}

interface ClientOption {
    value: number;
    label: string;
}

interface ShiftFormData {
    clientId: number;
    staffId: number;
    shiftType: 'regular' | 'respite' | 'emergency' | 'overnight' | 'sleepover';
    startDateTime: string;
    endDateTime: string;
    status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
    hourlyRate: number;
    notes: string;
    careInstructions: string;
    isSleepover: boolean;
}

const RosterCalendarPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [selectedStaffId, setSelectedStaffId] = useState<number | undefined>();
    const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
    const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Dialog states
    const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
    const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
    const [editMode, setEditMode] = useState(false);

    // Raw lists for the shared ShiftFormDialog (which builds its own rich labels).
    const [staffList, setStaffList] = useState<any[]>([]);
    const [clientList, setClientList] = useState<any[]>([]);
    // Pre-fill values when opening the dialog from a calendar day click.
    const [dialogInitialStart, setDialogInitialStart] = useState<string | undefined>();
    const [dialogInitialEnd, setDialogInitialEnd] = useState<string | undefined>();

    // Form data
    const [shiftForm, setShiftForm] = useState<ShiftFormData>({
        clientId: 0,
        staffId: 0,
        shiftType: 'regular',
        startDateTime: '',
        endDateTime: '',
        status: 'scheduled',
        hourlyRate: 25,
        notes: '',
        careInstructions: '',
        isSleepover: false
    });

    const userPrivilegeLevel = getUserPrivilegeLevel(user);
    const canEditShifts = userPrivilegeLevel >= 3; // Manager level or above

    const shiftTypes = [
        { value: 'regular', label: 'Regular' },
        { value: 'respite', label: 'Respite' },
        { value: 'emergency', label: 'Emergency' },
        { value: 'overnight', label: 'Overnight' },
        { value: 'sleepover', label: 'Sleepover (Inactive Overnight)' }
    ];

    const shiftStatuses = [
        { value: 'scheduled', label: 'Scheduled' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'no_show', label: 'No Show' }
    ];

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            console.log('Loading staff and client data...');
            
            const [staffData, clientData] = await Promise.all([
                staffService.getStaffProfiles(),
                clientService.getClients()
            ]);

            console.log('Raw staff data:', staffData);
            console.log('Raw client data:', clientData);

            // Handle paginated response format
            const staffArray = staffData?.results || staffData || [];
            const clientArray = clientData?.results || clientData || [];

            console.log('Staff array:', staffArray);
            console.log('Client array:', clientArray);

            const staffOpts = Array.isArray(staffArray) ? staffArray.map((staff: any) => {
                console.log('Processing staff:', staff);
                return {
                    value: staff.id,
                    label: getStaffDisplayName(staff)
                };
            }) : [];

            const clientOpts = Array.isArray(clientArray) ? clientArray.map((client: any) => {
                console.log('Processing client:', client);
                return {
                    value: client.id,
                    label: getClientDisplayName(client)
                };
            }) : [];

            console.log('Processed staff options:', staffOpts);
            console.log('Processed client options:', clientOpts);

            setStaffOptions(staffOpts);
            setClientOptions(clientOpts);
            setStaffList(Array.isArray(staffArray) ? staffArray : []);
            setClientList(Array.isArray(clientArray) ? clientArray : []);
        } catch (error) {
            console.error('Error loading data:', error);
            console.log('Using fallback data due to error');
            // Fallback to mock data
            setStaffOptions([
                { value: 1, label: 'John Smith - Care Worker' },
                { value: 2, label: 'Sarah Johnson - Nurse' },
                { value: 3, label: 'Mike Davis - Support Worker' },
                { value: 4, label: 'Emma Wilson - Care Assistant' }
            ]);
            setClientOptions([
                { value: 1, label: 'Mary Johnson' },
                { value: 2, label: 'Robert Brown' },
                { value: 3, label: 'Elizabeth Davis' },
                { value: 4, label: 'James Wilson' }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleShiftClick = (shift: Shift) => {
        setSelectedShift(shift);
        setEditMode(true);
        setDialogInitialStart(undefined);
        setDialogInitialEnd(undefined);
        setShiftDialogOpen(true);
    };

    const handleDateClick = (date: Dayjs) => {
        if (canEditShifts) {
            handleAddShift(date);
        }
    };

    const handleAddShift = (date: Dayjs) => {
        setSelectedShift(null);
        setEditMode(false);
        const startTime = date.hour(9).minute(0);
        const endTime = date.hour(17).minute(0);
        setDialogInitialStart(startTime.format('YYYY-MM-DDTHH:mm:ss'));
        setDialogInitialEnd(endTime.format('YYYY-MM-DDTHH:mm:ss'));
        setShiftDialogOpen(true);
    };

    const handleSaveShift = async () => {
        if (!shiftForm.clientId || !shiftForm.staffId || !shiftForm.startDateTime || !shiftForm.endDateTime) {
            setError('Please fill in all required fields');
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            // Pay/charge no longer comes from a per-shift hourly rate — the
            // value is computed at payroll/invoice time from Billing Rates.
            // Send 0 to satisfy the still-required column on the entity.
            const payload = { ...shiftForm, hourlyRate: 0 };
            if (editMode && selectedShift?.id) {
                await rosterService.updateShift(selectedShift.id, payload);
                setSuccessMessage('Shift updated successfully');
            } else {
                await rosterService.createShift(payload);
                setSuccessMessage('Shift created successfully');
            }
            setShiftDialogOpen(false);
            resetForm();
            setRefreshTrigger(prev => prev + 1); // Trigger calendar refresh
        } catch (error: any) {
            console.error('Error saving shift:', error);
            setError(error.response?.data?.message || 'Failed to save shift');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteShift = async () => {
        if (!selectedShift?.id) return;
        
        setLoading(true);
        try {
            await rosterService.deleteShift(selectedShift.id);
            setSuccessMessage('Shift deleted successfully');
            setShiftDialogOpen(false);
            resetForm();
            setRefreshTrigger(prev => prev + 1); // Trigger calendar refresh
        } catch (error: any) {
            console.error('Error deleting shift:', error);
            setError(error.response?.data?.message || 'Failed to delete shift');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setShiftForm({
            clientId: 0,
            staffId: 0,
            shiftType: 'regular',
            startDateTime: '',
            endDateTime: '',
            status: 'scheduled',
            hourlyRate: 25,
            notes: '',
            careInstructions: '',
            isSleepover: false
        });
        setSelectedShift(null);
        setEditMode(false);
    };

    const handleCloseDialog = () => {
        setShiftDialogOpen(false);
        resetForm();
        setError(null);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <PageLayout>
                <Box>
                    {/* Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h4" gutterBottom>
                            Roster Calendar
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<BackIcon />}
                            onClick={() => navigate('/roster')}
                        >
                            Back to List
                        </Button>
                    </Box>

                    {/* Alerts */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {successMessage && (
                        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
                            {successMessage}
                        </Alert>
                    )}

                    {/* Filter Controls */}
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Filter by Staff</InputLabel>
                                <Select
                                    value={selectedStaffId || ''}
                                    label="Filter by Staff"
                                    onChange={(e) => setSelectedStaffId(e.target.value ? Number(e.target.value) : undefined)}
                                >
                                    <MenuItem value="">All Staff</MenuItem>
                                    {staffOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            
                            {canEditShifts && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleAddShift(dayjs())}
                                >
                                    Add New Shift
                                </Button>
                            )}
                        </Box>
                    </Paper>

                    {/* Calendar View */}
                    <Paper sx={{ p: 2 }}>
                        {loading ? (
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center', 
                                minHeight: 400 
                            }}>
                                <Typography>Loading calendar data...</Typography>
                            </Box>
                        ) : (
                            <RosterCalendar
                                onShiftClick={handleShiftClick}
                                onDateClick={handleDateClick}
                                onAddShift={handleAddShift}
                                selectedStaffId={selectedStaffId}
                                readonly={!canEditShifts}
                                refreshTrigger={refreshTrigger}
                                staffOptions={staffOptions}
                                clientOptions={clientOptions}
                            />
                        )}
                    </Paper>

                    {/* Floating Action Button for Mobile */}
                    {canEditShifts && (
                        <Fab
                            color="primary"
                            aria-label="add"
                            sx={{
                                position: 'fixed',
                                bottom: 16,
                                right: 16,
                                display: { xs: 'flex', sm: 'none' }
                            }}
                            onClick={() => handleAddShift(dayjs())}
                        >
                            <AddIcon />
                        </Fab>
                    )}

                    {/* Shift Create/Edit Dialog (shared component) */}
                    <ShiftFormDialog
                        open={shiftDialogOpen}
                        onClose={handleCloseDialog}
                        shift={editMode ? selectedShift : null}
                        initialStart={dialogInitialStart}
                        initialEnd={dialogInitialEnd}
                        initialStaffId={selectedStaffId}
                        clients={clientList}
                        staff={staffList}
                        canEditShifts={canEditShifts}
                        onSaved={() => {
                            setShiftDialogOpen(false);
                            setSelectedShift(null);
                            setEditMode(false);
                            setSuccessMessage(editMode ? 'Shift updated successfully' : 'Shift created successfully');
                            setRefreshTrigger(prev => prev + 1);
                        }}
                    />
                </Box>
            </PageLayout>
        </LocalizationProvider>
    );
};

export default RosterCalendarPage;
